import React, { useState, useEffect } from 'react';

const ArtImagesManager = ({ token }) => {
  const [artworks, setArtworks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editingArt, setEditingArt] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    frame: 'blue',
    price: '',
    preorder: false,
    preorderGoal: '',
    downloadUrl: '',
    downloadEnabled: true,
    featured: false,
    available: true,
    files: []  // Changed from 'file' to 'files' for bulk
  });

  // Fetch existing artworks
  useEffect(() => {
    fetchArtworks();
  }, []);

  const fetchArtworks = async () => {
    try {
      const res = await fetch('/api/artupImages');
      const data = await res.json();
      setArtworks(data);
    } catch (err) {
      console.error('Error fetching artworks:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFormData(prev => ({ ...prev, files: selectedFiles }));
  };

  // Bulk upload handler
  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.files.length || !formData.title || !formData.frame) {
      alert('Please select files and fill in required fields');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    for (const file of formData.files) {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);
      uploadFormData.append('title', `${formData.title}${formData.files.length > 1 ? `_${successCount + 1}` : ''}`);
      uploadFormData.append('frame', formData.frame);
      uploadFormData.append('price', formData.price);
      uploadFormData.append('preorder', formData.preorder);
      uploadFormData.append('preorderGoal', formData.preorderGoal);
      uploadFormData.append('downloadUrl', formData.downloadUrl);
      uploadFormData.append('downloadEnabled', formData.downloadEnabled);
      uploadFormData.append('featured', formData.featured);
      uploadFormData.append('available', formData.available);

      try {
        const res = await fetch('/api/admin/upload-art', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: uploadFormData
        });

        if (res.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    alert(`✅ Upload complete: ${successCount} successful, ${failCount} failed`);
    
    setFormData({
      title: '',
      frame: 'blue',
      price: '',
      preorder: false,
      preorderGoal: '',
      downloadUrl: '',
      downloadEnabled: true,
      featured: false,
      available: true,
      files: []
    });
    
    fetchArtworks();
    document.getElementById('art-file-input').value = '';
    setUploading(false);
  };

  // Single upload handler
  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.files.length || !formData.title || !formData.frame) {
      alert('Please fill in all required fields and select an image');
      return;
    }

    setUploading(true);
    
    const uploadFormData = new FormData();
    uploadFormData.append('image', formData.files[0]);
    uploadFormData.append('title', formData.title);
    uploadFormData.append('frame', formData.frame);
    uploadFormData.append('price', formData.price);
    uploadFormData.append('preorder', formData.preorder);
    uploadFormData.append('preorderGoal', formData.preorderGoal);
    uploadFormData.append('downloadUrl', formData.downloadUrl);
    uploadFormData.append('downloadEnabled', formData.downloadEnabled);
    uploadFormData.append('featured', formData.featured);
    uploadFormData.append('available', formData.available);

    try {
      const res = await fetch('/api/admin/upload-art', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: uploadFormData
      });

      if (!res.ok) throw new Error('Upload failed');

      alert('✅ Artwork uploaded successfully!');
      setFormData({
        title: '',
        frame: 'blue',
        price: '',
        preorder: false,
        preorderGoal: '',
        downloadUrl: '',
        downloadEnabled: true,
        featured: false,
        available: true,
        files: []
      });
      fetchArtworks();
      
      // Clear file input
      document.getElementById('art-file-input').value = '';
      
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this artwork?')) return;
    
    try {
      const res = await fetch(`/api/admin/upload-art/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Delete failed');
      
      alert('✅ Artwork deleted successfully');
      fetchArtworks();
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleEdit = (art) => {
    setEditingArt(art);
    setFormData({
      title: art.title,
      frame: art.frame,
      price: art.price || '',
      preorder: art.preorder || false,
      preorderGoal: art.preorderGoal || '',
      downloadUrl: art.downloadUrl || '',
      downloadEnabled: art.downloadEnabled !== false,
      featured: art.featured || false,
      available: art.available !== false,
      files: []
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    const updateData = {
      title: formData.title,
      frame: formData.frame,
      price: formData.price,
      preorder: formData.preorder,
      preorderGoal: formData.preorderGoal,
      downloadUrl: formData.downloadUrl,
      downloadEnabled: formData.downloadEnabled,
      featured: formData.featured,
      available: formData.available
    };

    try {
        const res = await fetch(`/api/admin/upload-art/${id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (!res.ok) throw new Error('Update failed');
      
      alert('✅ Artwork updated successfully');
      setEditingArt(null);
      setFormData({
        title: '',
        frame: 'blue',
        price: '',
        preorder: false,
        preorderGoal: '',
        downloadUrl: '',
        downloadEnabled: true,
        featured: false,
        available: true,
        files: []
      });
      fetchArtworks();
    } catch (error) {
      alert(`❌ Error: ${error.message}`);
    }
  };

  return (
    <div className="p-6 bg-white rounded shadow">
      <h2 className="text-2xl font-bold mb-4">Digital Art Manager</h2>
      
      {/* Bulk Mode Toggle */}
      <div className="mb-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={bulkMode}
            onChange={(e) => setBulkMode(e.target.checked)}
          />
          <span className="font-semibold">Bulk Upload Mode</span>
          <span className="text-sm text-gray-500">(Upload multiple images at once)</span>
        </label>
      </div>
      
      {/* Upload/Edit Form */}
      <form onSubmit={editingArt ? handleUpdate : (bulkMode ? handleBulkSubmit : handleSingleSubmit)} className="space-y-4 mb-8 p-4 border rounded">
        <h3 className="text-lg font-semibold">
          {editingArt ? 'Edit Artwork' : (bulkMode ? 'Bulk Upload Artworks' : 'Upload New Artwork')}
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {bulkMode ? 'Base Title *' : 'Title *'}
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="border px-3 py-2 rounded w-full"
              placeholder={bulkMode ? "Enter base title (e.g., 'Sunset Series')" : "Enter artwork title"}
            />
            {bulkMode && (
              <p className="text-xs text-gray-500 mt-1">
                Files will be named: Title_1, Title_2, Title_3...
              </p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Frame Color *</label>
            <select
              name="frame"
              value={formData.frame}
              onChange={handleInputChange}
              required
              className="border px-3 py-2 rounded w-full"
            >
              <option value="blue">Blue Frame</option>
              <option value="black">Black Frame</option>
              <option value="gold">Gold Frame</option>
              <option value="redwood">Redwood Frame</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Price ($) *</label>
            <input
              type="number"
              step="0.01"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              required
              className="border px-3 py-2 rounded w-full"
              placeholder="0.00"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Preorder Goal</label>
            <input
              type="number"
              step="1"
              name="preorderGoal"
              value={formData.preorderGoal}
              onChange={handleInputChange}
              className="border px-3 py-2 rounded w-full"
              placeholder="Target preorder count (optional)"
            />
          </div>
          
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Download URL</label>
            <input
              type="url"
              name="downloadUrl"
              value={formData.downloadUrl}
              onChange={handleInputChange}
              className="border px-3 py-2 rounded w-full"
              placeholder="https://your-cdn.com/file.zip or /api/download/file-id"
            />
            <p className="text-xs text-gray-500 mt-1">URL where customers can download the digital file</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="preorder"
              checked={formData.preorder}
              onChange={handleInputChange}
            />
            Available for Preorder
          </label>
          
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="downloadEnabled"
              checked={formData.downloadEnabled}
              onChange={handleInputChange}
            />
            Enable Digital Download
          </label>
          
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="featured"
              checked={formData.featured}
              onChange={handleInputChange}
            />
            Featured Artwork
          </label>
          
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="available"
              checked={formData.available}
              onChange={handleInputChange}
            />
            Available for Purchase
          </label>
        </div>
        
        {!editingArt && (
          <div>
            <label className="block text-sm font-medium mb-1">
              {bulkMode ? 'Image Files (Select Multiple) *' : 'Image File *'}
            </label>
            <input
              id="art-file-input"
              type="file"
              accept="image/*"
              multiple={bulkMode}
              onChange={handleFileChange}
              required
              className="border px-3 py-2 rounded w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              {bulkMode 
                ? 'Select multiple images (CTRL+Click or SHIFT+Click)' 
                : 'Upload artwork preview image (JPG, PNG, GIF)'}
            </p>
            {formData.files.length > 0 && (
              <p className="text-sm text-green-600 mt-1">
                {formData.files.length} file(s) selected
              </p>
            )}
          </div>
        )}
        
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={uploading}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {uploading ? 'Processing...' : (editingArt ? 'Update Artwork' : (bulkMode ? `Upload ${formData.files.length} Files` : 'Upload Artwork'))}
          </button>
          
          {editingArt && (
            <button
              type="button"
              onClick={() => {
                setEditingArt(null);
                setFormData({
                  title: '',
                  frame: 'blue',
                  price: '',
                  preorder: false,
                  preorderGoal: '',
                  downloadUrl: '',
                  downloadEnabled: true,
                  featured: false,
                  available: true,
                  files: []
                });
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>
      
      {/* Artworks Gallery */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Digital Artworks ({artworks.length})</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {artworks.map((art) => (
            <div key={art.id} className="border rounded p-2 relative">
              <img
                src={art.image}
                alt={art.title}
                className="w-full h-40 object-cover rounded mb-2"
              />
              <div className="text-sm">
                <p className="font-semibold truncate">{art.title}</p>
                <p className="text-gray-600 capitalize">{art.frame} frame</p>
                <p className="text-green-600 font-bold">${art.price}</p>
                {art.preorder && <p className="text-orange-500 text-xs">Preorder: {art.preorderGoal || 'No goal'} units</p>}
                {art.downloadEnabled ? (
                  <p className="text-blue-500 text-xs">✓ Digital Download</p>
                ) : (
                  <p className="text-gray-400 text-xs">Download disabled</p>
                )}
              </div>
              <div className="flex gap-1 mt-2">
                <button
                  onClick={() => handleEdit(art)}
                  className="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(art.id)}
                  className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ArtImagesManager;